import React, { useEffect, useState, Component } from 'react';
import { MarketProductFit } from './fits/MarketProductFit';
import { ProductChannelFit } from './fits/ProductChannelFit';
import { ChannelModelFit } from './fits/ChannelModelFit';
import { ModelMarketFit } from './fits/ModelMarketFit';
import { Dashboard } from './Dashboard';
import { CtaSection } from './CtaSection';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export const Simulator = () => {
  const [activeSection, setActiveSection] = useState(0);
  // State for all simulator values
  const [simulationData, setSimulationData] = useState({
    // Market ↔ Product Fit
    timeToValue: 3,
    stickiness: 4,
    valuePropBroadness: 'Medium',
    // Product ↔ Channel Fit
    primaryChannel: 'Outbound Sales (CAC: $5,000)',
    // Channel ↔ Model Fit
    arpu: 5000,
    // Model ↔ Market Fit
    tam: 100000,
    captureRate: 4,
    // Calculated values
    alignmentScore: 80,
    ltv: 15000,
    cac: 5000,
    ltvCacRatio: 3.0,
    paybackPeriod: 12.0,
    projectedRevenue: 20000000,
    isSmoothSailor: true
  });
  // Update simulation data
  const updateSimulation = newData => {
    setSimulationData(prevData => {
      const updatedData = {
        ...prevData,
        ...newData
      };
      // Calculate derived metrics
      const ltvCacRatio = updatedData.ltv / updatedData.cac;
      const alignmentScore = calculateAlignmentScore(updatedData);
      const projectedRevenue = calculateProjectedRevenue(updatedData);
      const isSmoothSailor = alignmentScore >= 70;
      return {
        ...updatedData,
        ltvCacRatio,
        alignmentScore,
        projectedRevenue,
        isSmoothSailor
      };
    });
  };
  // Calculate alignment score based on all parameters
  const calculateAlignmentScore = data => {
    let score = 0;
    const mpScore = (5 - data.timeToValue) * 3 + data.stickiness * 2;
    score += Math.min(25, mpScore);
    const pcScore = data.primaryChannel.includes('Outbound') ? 20 : 15;
    score += pcScore;
    const cmScore = data.arpu >= 5000 ? 25 : data.arpu / 200;
    score += Math.min(25, cmScore);
    const mmScore = data.tam / 10000 * (data.captureRate / 2);
    score += Math.min(25, mmScore);
    return Math.round(score);
  };
  // Calculate projected revenue
  const calculateProjectedRevenue = data => {
    return data.tam * (data.captureRate / 100) * data.arpu;
  };
  const sections = [{
    id: 0,
    title: 'Market ↔ Product Fit',
    color: 'blue',
    component: MarketProductFit,
    props: {
      timeToValue: simulationData.timeToValue,
      stickiness: simulationData.stickiness,
      valuePropBroadness: simulationData.valuePropBroadness,
      updateSimulation
    }
  }, {
    id: 1,
    title: 'Product ↔ Channel Fit',
    color: 'purple',
    component: ProductChannelFit,
    props: {
      primaryChannel: simulationData.primaryChannel,
      updateSimulation
    }
  }, {
    id: 2,
    title: 'Channel ↔ Model Fit',
    color: 'green',
    component: ChannelModelFit,
    props: {
      arpu: simulationData.arpu,
      updateSimulation,
      cac: simulationData.cac,
      ltvCacRatio: simulationData.ltvCacRatio
    }
  }, {
    id: 3,
    title: 'Model ↔ Market Fit',
    color: 'yellow',
    component: ModelMarketFit,
    props: {
      tam: simulationData.tam,
      captureRate: simulationData.captureRate,
      updateSimulation,
      projectedRevenue: simulationData.projectedRevenue
    }
  }];
  const toggleSection = (index: number) => {
    setActiveSection(activeSection === index ? -1 : index);
  };
  const goToNext = () => {
    if (activeSection < sections.length - 1) {
      setActiveSection(activeSection + 1);
    }
  };
  return <div className="container mx-auto px-4 py-8 max-w-7xl" data-id="element-209">
      <header className="mb-8" data-id="element-210">
        <div className="flex items-center justify-between" data-id="element-211">
          <div data-id="element-212">
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent" data-id="element-213">
              4-Fit Framework Simulator
            </h1>
            <p className="text-gray-400 mt-2" data-id="element-214">
              Visualize your strategic path to $100M+ revenue
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-215">
            <button className="bg-opacity-20 bg-white backdrop-blur-sm px-4 py-2 rounded-md text-sm flex items-center space-x-2 hover:bg-opacity-30 transition" data-id="element-216">
              <span data-id="element-217">Reset</span>
            </button>
          </div>
        </div>
      </header>
      <div className="flex flex-col lg:flex-row gap-6" data-id="element-218">
        {/* Left Panel - 60% */}
        <div className="lg:w-3/5 space-y-4" data-id="element-219">
          {/* Progress Indicator */}
          <div className="bg-[#1E293B] bg-opacity-60 backdrop-blur-md rounded-xl p-4 border border-[#334155]" data-id="element-220">
            <div className="flex items-center justify-between mb-2" data-id="element-221">
              <span className="text-sm text-gray-400" data-id="element-222">Your Progress</span>
              <span className="text-sm font-semibold text-white" data-id="element-223">
                Step {activeSection + 1} of {sections.length}
              </span>
            </div>
            <div className="flex gap-2" data-id="element-224">
              {sections.map((section, index) => <div key={section.id} className={`flex-1 h-2 rounded-full transition-all duration-300 ${index <= activeSection ? `bg-${section.color}-500` : 'bg-gray-700'}`} data-id="element-225" />)}
            </div>
          </div>
          {/* Accordion Sections */}
          <div className="space-y-3" data-id="element-226">
            {sections.map((section, index) => {
            const Component = section.component;
            const isActive = activeSection === index;
            const isCompleted = index < activeSection;
            return <div key={section.id} className={`bg-[#1E293B] bg-opacity-60 backdrop-blur-md rounded-xl border transition-all duration-300 ${isActive ? `border-${section.color}-500 shadow-lg shadow-${section.color}-500/20` : 'border-[#334155]'}`} data-id="element-227">
                  {/* Section Header */}
                  <button onClick={() => toggleSection(index)} className="w-full p-4 flex items-center justify-between hover:bg-white hover:bg-opacity-5 transition-colors rounded-t-xl" data-id="element-228">
                    <div className="flex items-center" data-id="element-229">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 transition-colors ${isActive ? `bg-${section.color}-500 text-white` : isCompleted ? `bg-${section.color}-500 bg-opacity-20 text-${section.color}-500` : 'bg-gray-700 text-gray-400'}`} data-id="element-230">
                        <span className="text-sm font-bold" data-id="element-231">{index + 1}</span>
                      </div>
                      <h2 className={`text-lg font-bold transition-colors ${isActive ? `text-${section.color}-500` : 'text-gray-300'}`} data-id="element-232">
                        {section.title}
                      </h2>
                    </div>
                    {isActive ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-233" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-234" />}
                  </button>
                  {/* Section Content */}
                  <div className={`overflow-hidden transition-all duration-300 ${isActive ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-235">
                    <div className="p-6 pt-2" data-id="element-236">
                      <Component {...section.props} data-id="element-237" />
                      {/* Next Button */}
                      {isActive && index < sections.length - 1 && <div className="mt-6 flex justify-end" data-id="element-238">
                          <button onClick={goToNext} className={`bg-${section.color}-500 hover:bg-${section.color}-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center`} data-id="element-239">
                            Continue to Next Step
                            <ChevronDownIcon className="w-4 h-4 ml-2" data-id="element-240" />
                          </button>
                        </div>}
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
        {/* Right Panel - 40% */}
        <div className="lg:w-2/5" data-id="element-241">
          <div className="sticky top-4" data-id="element-242">
            <Dashboard simulationData={simulationData} data-id="element-243" />
          </div>
        </div>
      </div>
      <CtaSection data-id="element-244" />
    </div>;
};